import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_WORKING_HOURS_RESOURCE } from 'src/features/workingHours/api/useListWorkingHours';
import { useUpdateWorkingHours } from 'src/features/workingHours/api/useUpdateWorkingHours';
import WorkingHoursFormModal from 'src/features/workingHours/components/form/WorkingHoursFormModal';
import { WorkingHours } from 'src/features/workingHours/utils/types';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { ICON_MAP } from 'src/utils/constants';

interface WorkingHoursFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedWorkingHours: WorkingHours;
    children?: React.ReactNode;
}

function UpdateWorkingHoursFormModal<TData>({
    className,
    semantics,
    children,
    selectedWorkingHours,
}: WorkingHoursFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { siteId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateWorkingHours({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_WORKING_HOURS_RESOURCE] });
            toast({
                title: formatMessage(workingHoursMessages.updateWorkingHoursSuccess),
                description: formatMessage(workingHoursMessages.updateWorkingHoursSuccessDescription),
            });
        },
    });

    return (
        <WorkingHoursFormModal
            selectedWorkingHours={selectedWorkingHours}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, hoursId: selectedWorkingHours.id, siteId, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(workingHoursMessages.editWorkingHoursSubtitle)}
        >
            {children}
        </WorkingHoursFormModal>
    );
}

export default UpdateWorkingHoursFormModal;
